import { useState } from "react";

import styles from "./Settings.module.css";
import { useSession } from "../../auth/session";
import { Avatar } from "../../components/Avatar";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Pill } from "../../components/Pill";
import { Select } from "../../components/Select";
import { ApiError } from "../../data/api/client";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import {
  changeMemberRole,
  inviteMember,
  type MemberRole,
  removeMember,
  revokeInvitation,
  useInvitations,
  useMembers,
} from "../../data/api/workspace";
import { Icon } from "../../icons";
import { relTime } from "../../lib/time";

const INVITE_ROLE_OPTS = [
  { label: "Member", value: "member" },
  { label: "Admin", value: "admin" },
];
const MEMBER_ROLE_OPTS = [
  { label: "Owner", value: "owner" },
  { label: "Admin", value: "admin" },
  { label: "Member", value: "member" },
];

export const Team = () => {
  const { user } = useSession();
  const { workspace } = useCurrentWorkspace();
  const wsId = workspace?.public_id ?? null;
  const { members, refetch } = useMembers(wsId);
  const { invitations, refetch: refetchInvites } = useInvitations(wsId);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [busy, setBusy] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const myRole = members.find((m) => m.public_id === user?.public_id)?.role;
  const canManage = myRole === "owner" || myRole === "admin";
  const ownerCount = members.filter((m) => m.role === "owner").length;

  const run = (
    key: string,
    fn: () => Promise<unknown>,
    after: () => void,
  ): void => {
    setBusy(key);
    setError(null);
    void fn()
      .then(after)
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError ? err.message : "Something went wrong.",
        ),
      )
      .finally(() => setBusy(null));
  };

  const doInvite = (): void => {
    if (!email.trim() || !wsId) {
      return;
    }
    setBusy("invite");
    setError(null);
    setInviteLink(null);
    void inviteMember(wsId, { email: email.trim(), role })
      .then((res) => {
        setInviteLink(
          `${window.location.origin}/join?token=${res.invitation.token}`,
        );
        setEmail("");
        refetchInvites();
      })
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError ? err.message : "Couldn't send the invite.",
        ),
      )
      .finally(() => setBusy(null));
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Team</h3>
      <div className={styles.card}>
        {members.map((m) => {
          const isSelf = m.public_id === user?.public_id;
          const soleOwner = m.role === "owner" && ownerCount <= 1;
          const locked = !canManage || isSelf || soleOwner;
          return (
            <div className={styles.row} key={m.public_id}>
              <Avatar name={m.display_name} size={30} />
              <div className={styles.grow}>
                <div className={styles.label}>
                  {m.display_name}
                  {isSelf ? <span className={styles.help}> · you</span> : null}
                </div>
                <div className={styles.help}>{m.email}</div>
              </div>
              {canManage && !isSelf ? (
                <Button
                  disabled={busy === `rm:${m.public_id}` || soleOwner}
                  onClick={() =>
                    run(
                      `rm:${m.public_id}`,
                      () => removeMember(wsId!, m.public_id),
                      refetch,
                    )
                  }
                  size="sm"
                  style={{ marginRight: 8 }}
                  title={
                    soleOwner ? "The sole owner can't be removed" : "Remove"
                  }
                  variant="ghost"
                >
                  Remove
                </Button>
              ) : null}
              {locked ? (
                <Pill status={m.role === "owner" ? "done" : "review"}>
                  {m.role}
                </Pill>
              ) : (
                <Select
                  onValueChange={(v) =>
                    run(
                      `role:${m.public_id}`,
                      () =>
                        changeMemberRole(wsId!, m.public_id, v as MemberRole),
                      refetch,
                    )
                  }
                  options={MEMBER_ROLE_OPTS}
                  value={m.role}
                />
              )}
            </div>
          );
        })}

        {invitations.map((inv) => (
          <div className={styles.row} key={inv.public_id}>
            <span className={styles.inviteIcon}>
              <Icon.Bell size={15} />
            </span>
            <div className={styles.grow}>
              <div className={styles.label}>{inv.email}</div>
              <div className={styles.help}>
                invited {relTime(Date.parse(inv.created_at))} · {inv.role}
              </div>
            </div>
            {canManage ? (
              <Button
                disabled={busy === `rev:${inv.public_id}`}
                onClick={() =>
                  run(
                    `rev:${inv.public_id}`,
                    () => revokeInvitation(wsId!, inv.public_id),
                    refetchInvites,
                  )
                }
                size="sm"
                style={{ marginRight: 8 }}
                variant="ghost"
              >
                Revoke
              </Button>
            ) : null}
            <Pill dotColor="var(--ink-4)">invited</Pill>
          </div>
        ))}

        {canManage ? (
          <div className={styles.row}>
            <Input
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  doInvite();
                }
              }}
              placeholder="teammate@company.com"
              style={{ flex: 1 }}
              type="email"
              value={email}
            />
            <Select
              onValueChange={(v) => setRole(v as "admin" | "member")}
              options={INVITE_ROLE_OPTS}
              value={role}
            />
            <Button
              disabled={!email.trim() || busy === "invite"}
              onClick={doInvite}
              size="sm"
              variant="accent"
            >
              <Icon.Plus size={13} /> Invite
            </Button>
          </div>
        ) : null}

        {inviteLink ? (
          <div className={styles.row}>
            <div className={styles.grow}>
              <div className={styles.help}>
                We emailed a join link. You can also share it directly:
              </div>
              <code className={styles.joinLink}>{inviteLink}</code>
            </div>
            <Button
              onClick={() => void navigator.clipboard?.writeText(inviteLink)}
              size="sm"
              title="Copy link"
              variant="ghost"
            >
              <Icon.Copy size={13} /> Copy
            </Button>
          </div>
        ) : null}

        {error ? (
          <div className={styles.row}>
            <span
              className={styles.help}
              style={{ color: "var(--needs-input)" }}
            >
              {error}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
};
